import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AiTriage } from "@/components/AiTriage";
import { FaultReadingChart } from "@/components/FaultReadingChart";
import { FaultRateCard } from "@/components/FaultRateCard";
import { FaultDailyProfile } from "@/components/FaultDailyProfile";
import { FaultDetailHeader } from "@/components/FaultDetailHeader";

function Readings({ data, err }) {
  if (err) return <div className="mt-3 text-sm text-red-600">{err}</div>;
  if (!data) return <div className="mt-3 k-sublabel">Loading readings…</div>;
  if ((data.readings || []).length === 0) {
    return (
      <div className="mt-3 text-sm text-[#6B7280]" data-testid="fault-detail-no-readings">
        No stored history for this equipment in the selected period.
      </div>
    );
  }
  return data.readings.map((r) => <FaultReadingChart key={r.point_id} reading={r} />);
}

/** Slide-over showing a fault's daily profile, description and the readings behind it. */
export function FaultDetail({ fault, start, days, isAdmin, onClose, onRuleChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const saveRule = useCallback(async (patch) => {
    setBusy(true);
    try {
      await api.patch(`/faults/rules/${encodeURIComponent(fault.rule_id)}`, {
        critical: patch.critical ?? fault.critical,
        notify: patch.notify ?? (fault.notify !== false),
        cost_rate: patch.cost_rate !== undefined ? patch.cost_rate : (fault.cost_source === "portal" ? fault.cost_rate : null),
      });
      toast.success(patch.message || "Rule updated");
      onRuleChanged();
    } catch (e) {
      toast.error("Could not update rule", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  }, [fault, onRuleChanged]);

  useEffect(() => {
    setData(null);
    setErr(null);
    api
      .get("/faults/detail", { params: { site_id: fault.site_id, rule_id: fault.rule_id, start, days } })
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [fault, start, days]);

  const toggleCritical = () =>
    saveRule({
      critical: !fault.critical,
      message: !fault.critical ? "Marked critical — you'll be emailed when it fires" : "No longer critical",
    });

  const saveRate = (value) => saveRule({ cost_rate: value, message: "Cost rate saved" });

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="fault-detail-panel">
      <div className="absolute inset-0 bg-[#212121]/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl" style={{ animation: "slideIn .25s ease" }}>
        <FaultDetailHeader
          fault={fault}
          isAdmin={isAdmin}
          busy={busy}
          onToggleCritical={toggleCritical}
          onClose={onClose}
        />

        <div className="px-6 py-5">
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-md bg-[#F3F8F7] p-4">
              <div className="k-sublabel">Duration</div>
              <div className="mt-1 text-lg font-heading font-semibold text-[#212121]">{fault.hours.toFixed(2)} hr</div>
            </div>
            <div className="rounded-md bg-[#F3F8F7] p-4">
              <div className="k-sublabel">Impact</div>
              <div className="mt-1 text-lg font-heading font-semibold text-[#212121]">
                {fault.cost ? `$${fault.cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
              </div>
            </div>
            <FaultRateCard fault={fault} isAdmin={isAdmin} busy={busy} onSaveRate={saveRate} />
          </div>

          <AiTriage fault={fault} />

          {fault.help && (
            <div className="mt-5 rounded-md border border-[#E5E7EB] p-4 text-sm text-[#4D4D4D] whitespace-pre-line" data-testid="fault-detail-help">
              {fault.help}
            </div>
          )}

          <FaultDailyProfile days={fault.days} label={fault.rule_dis || "fault"} />

          <div className="mt-8">
            <div className="k-label">Readings behind this fault</div>
            <Readings data={data} err={err} />
          </div>
        </div>
      </div>
    </div>
  );
}
